import to from 'await-to-js';
import { Component, ContentType, Project } from 'contensis-core-api';
import { Node } from 'contensis-delivery-api';
import { Entry, Tag, TagGroup } from 'contensis-management-api';
import ApiKeyService from './ApiKeyService';
import BlocksService from './BlocksService';
import CopyFieldService from './CopyFieldService';
import EntriesMigrationService from './EntriesMigrationService';
import ModelMigrationService from './ModelMigrationService';
import NodesMigrationService from './NodesMigrationService';
import ProjectsService from './ProjectsService';
import ProxiesService from './ProxiesService';
import RenderersService from './RenderersService';
import RolesMigrationService from './RolesMigrationService';
import SimpleMigrationService from './SimpleMigrationService';
import { GetTagGroupsArgs } from './TagGroupsMigrationService';
import TagsMigrationService, { GetTagsArgs } from './TagsMigrationService';
import UpdateFieldService from './UpdateFieldService';
import WebhookMigrationService from './WebhookMigrationService';
import WebhookSubscriptionService from './WebhookSubscriptionService';
import { Model } from '~/models';
import { IContentMigrationServices } from '~/models/IContentMigrationServices';
import {
  ContentTypesResult,
  EntriesMigrationResult,
  EntriesResult,
  MigrateRequest,
  NodesResult,
  TagGroupsResult,
  TagsResult,
} from '~/models/Operations';
import { mapMigrateEnvironmentResponse } from '~/transformations/mapServiceResponsesToMigrateEverythingResponse';
import { Logger } from '~/util/logger';

type ContentModelsResult = {
  models: Model[];
  contentTypes: ContentType[];
  components: Component[];
  entries: Entry[];
  nodes: Node[];
  project?: Project;
  tagGroups: TagGroup[];
};

/**
 * The purpose of ContensisMigrationService is to act as the public interface
 * for all Contensis migrations performed by the CLI or imported from the npm package
 */
class ContensisMigrationService {
  simpleMigration: SimpleMigrationService;
  payload: MigrateRequest;
  isPreview: boolean;
  models: ModelMigrationService;
  content: IContentMigrationServices;
  nodes: NodesMigrationService;
  projects: ProjectsService;
  apiKeys: ApiKeyService;
  blocks: BlocksService;
  proxies: ProxiesService;
  renderers: RenderersService;
  roles: RolesMigrationService;
  subscriptions: {
    webhooks: WebhookSubscriptionService;
  };
  tags: TagsMigrationService;
  webhooks: WebhookMigrationService;

  constructor(payload: MigrateRequest, preview = false) {
    this.payload = payload;
    this.isPreview = preview;
    this.simpleMigration = new SimpleMigrationService(payload, preview);
    this.content = Object.assign(
      new EntriesMigrationService(payload, preview),
      {
        copy: new CopyFieldService(payload, preview),
        update: new UpdateFieldService(payload, preview),
      }
    );
    this.models = new ModelMigrationService(payload, preview);
    this.nodes = new NodesMigrationService(payload, preview);
    this.projects = new ProjectsService(payload, preview);
    this.roles = new RolesMigrationService(payload, preview);
    this.tags = new TagsMigrationService(payload, preview);
    this.webhooks = new WebhookMigrationService(payload, preview);
    this.apiKeys = this.source.apiKeys;
    this.blocks = this.source.blocks;
    this.proxies = this.source.proxies;
    this.renderers = this.source.renderers;
    this.subscriptions = { webhooks: this.source.webhooks };
  }

  get contensisVersion() {
    return this.source.repo.ContensisVersion();
  }

  // A safe view of the repos connected to entries service
  // accessing only elements that exist on instantiation
  get repos() {
    return {
      source: {
        alias: this.source.alias,
        project: this.source.projectId,
        query: this.source.query,
        zenQL: this.source.zenQL,
      },
      target: {
        alias: Object.values(this.targets)?.[0].alias,
        projects: Object.fromEntries(
          Object.entries(this.targets).map(([project, target]) => [
            project,
            {
              query: target.query,
              zenQL: target.zenQL,
            },
          ])
        ),
      },
    };
  }

  get source() {
    return this.projects.source;
  }

  get targets() {
    return this.projects.targets;
  }

  GetContentModels = async (
    modelIds = this.payload.models || []
  ): Promise<ContentModelsResult> => {
    const [hydrateError] = await to(
      this.models.HydrateContensisRepositories(modelIds)
    );

    if (hydrateError) throw hydrateError;

    const {
      models,
      contentTypes,
      components,
      entries,
      nodes,
      project,
      tagGroups,
    } = this.models.source.context;

    return {
      models,
      contentTypes,
      components,
      entries,
      nodes,
      project,
      tagGroups,
    };
  };

  GetEntries = async (options: { withDependents: boolean }) =>
    await this.content.GetEntries(options);

  GetNodes = async (rootPath?: string, depth?: number) =>
    await this.nodes.GetNodes(rootPath, depth);

  GetTags = async <T extends boolean = false>({
    withDependents,
    ...query
  }: GetTagsArgs & { withDependents: T }) => {
    const [error, tags] = await this.tags.GetTags(query, { withDependents });
    if (error) throw error;
    return tags as T extends true ? (Tag | TagGroup)[] : Tag[];
  };

  GetTagGroups = async (query?: GetTagGroupsArgs) => {
    const [error, tagGroups] = await this.tags.GetTagGroups(query);
    if (error) throw error;
    return tagGroups;
  };

  MigrateEnvironment = async () => {
    // const [assetsError, assets] = await this.MigrateAssets();
    const [modelsError, models] = await this.MigrateContentModels();
    const [entriesError, entries] = await this.MigrateEntries();
    const [nodesError, nodes] = await this.MigrateNodes();

    const { payload, isPreview } = this;
    const response = mapMigrateEnvironmentResponse({
      errors: [modelsError, entriesError, nodesError].filter((e) => e),
      models,
      entries,
      nodes,
      payload,
      isPreview,
    });
    return response;
  };

  MigrateAssets = async () => {
    const [error, assetImport] = await to(this.content.MigrateContentAssets());
    if (error) Logger.error(`[assets migration] ${JSON.stringify(error)}`);
    return [error, assetImport] as [AppError | null, EntriesResult | undefined];
  };

  MigrateContentModels = async (models = this.payload.models || []) => {
    const [error, modelsImport] = await to(this.models.MigrateModels(models));
    if (error) Logger.error(`[models migration] ${JSON.stringify(error)}`);
    return [error, modelsImport] as [
      AppError | null,
      ContentTypesResult | undefined,
    ];
  };

  MigrateEntries = async () => {
    const [error, entriesImport] = await to(this.content.MigrateEntries());
    if (error) Logger.error(`[entries migration] ${JSON.stringify(error)}`);
    return [error, entriesImport] as [
      AppError | null,
      EntriesMigrationResult | undefined,
    ];
  };

  MigrateNodes = async () => {
    const [error, nodesImport] = await to(this.nodes.MigrateNodes());
    if (error) Logger.error(`[nodes migration] ${JSON.stringify(error)}`);
    return [error, nodesImport] as [AppError | null, NodesResult | undefined];
  };

  MigrateTags = async (query?: GetTagsArgs) => {
    const [error, tagsImport] = await to(this.tags.MigrateTags(query));
    if (error) Logger.error(`[tags migration] ${JSON.stringify(error)}`);
    return [error, tagsImport] as [AppError | null, TagsResult | undefined];
  };

  MigrateTagGroups = async (query?: GetTagGroupsArgs) => {
    const [error, groupsImport] = await to(this.tags.MigrateTagGroups(query));
    if (error) Logger.error(`[tag groups migration] ${JSON.stringify(error)}`);
    return [error, groupsImport] as [
      AppError | null,
      TagGroupsResult | undefined,
    ];
  };

  DeleteNodes = async (rootPaths?: string | string[]) => {
    const [error, nodesImport] = await to(
      this.nodes.DeleteNodes(
        typeof rootPaths === 'string' ? [rootPaths] : rootPaths
      )
    );
    if (error) Logger.error(`[delete nodes] ${JSON.stringify(error)}`);
    return [error, nodesImport] as [AppError | null, NodesResult | undefined];
  };

  DeleteEntries = async (recycle = false) => {
    const [error, entriesImport] = await to(
      this.content.DeleteEntries(recycle)
    );
    if (error) Logger.error(`[delete entries] ${JSON.stringify(error)}`);
    return [error, entriesImport] as [
      AppError | null,
      EntriesResult | undefined,
    ];
  };

  DeleteAssets = async () => {
    const [error, assetImport] = await to(this.content.DeleteAssetEntries());
    if (error) Logger.error(`[delete assets] ${JSON.stringify(error)}`);
    return [error, assetImport] as [AppError | null, EntriesResult | undefined];
  };

  DeleteContentTypes = async (
    contentTypeIds = this.payload.contentTypes,
    componentIds = this.payload.components
  ) => {
    // TODO: implement a safe system for deleting everything, e.g. ['*']
    const [error, modelsImport] = await to(
      this.simpleMigration.Delete(contentTypeIds, componentIds)
    );
    if (error) Logger.error(`[delete content models] ${JSON.stringify(error)}`);
    return [error, modelsImport] as [
      AppError | null,
      typeof modelsImport | undefined,
    ];
  };

  DeleteTags = async (query: GetTagsArgs = {}) => {
    const [error, tagsImport] = await to(this.tags.DeleteTags(query));
    if (error) Logger.error(`[delete tags] ${JSON.stringify(error)}`);
    return [error, tagsImport] as [AppError | null, TagsResult | undefined];
  };

  DeleteTagGroups = async (query: GetTagGroupsArgs = {}) => {
    const [error, groupsImport] = await to(this.tags.DeleteTagGroups(query));
    if (error) Logger.error(`[delete tag groups] ${JSON.stringify(error)}`);
    return [error, groupsImport] as [
      AppError | null,
      TagGroupsResult | undefined,
    ];
  };
}

export default ContensisMigrationService;
